import { inject, Injectable } from '@angular/core';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { HUBSPOT_DISTRIBUTORS_COLLECTION, HUBSPOT_OWNERS_COLLECTION, logger } from '@damofire/data-commons';

export interface HubspotDistributor {
  label: string;
  value: string;
}

export interface HubspotOwner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class HubspotConfigLookupService {
  private readonly firestore = inject(Firestore);

  private distributorsCache: CacheEntry<HubspotDistributor[]> | null = null;
  private ownersCache: CacheEntry<HubspotOwner[]> | null = null;

  private isFresh<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
    return entry !== null && Date.now() - entry.fetchedAt < CACHE_TTL_MS;
  }

  async getDistributors(): Promise<HubspotDistributor[]> {
    if (this.isFresh(this.distributorsCache)) {
      return this.distributorsCache.data;
    }

    const docRef = doc(this.firestore, `${HUBSPOT_DISTRIBUTORS_COLLECTION}/distributors`);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      logger.warn('hubspot-distributors/distributors document not found', { category: 'hubspot' });
      return [];
    }

    const data = docSnap.data() as { distributors: HubspotDistributor[] };
    this.distributorsCache = { data: data.distributors ?? [], fetchedAt: Date.now() };
    return this.distributorsCache.data;
  }

  async getOwners(): Promise<HubspotOwner[]> {
    if (this.isFresh(this.ownersCache)) {
      return this.ownersCache.data;
    }

    const docRef = doc(this.firestore, `${HUBSPOT_OWNERS_COLLECTION}/owners`);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      logger.warn('hubspot-owners/owners document not found', { category: 'hubspot' });
      return [];
    }

    const data = docSnap.data() as { owners: HubspotOwner[] };
    this.ownersCache = { data: data.owners ?? [], fetchedAt: Date.now() };
    return this.ownersCache.data;
  }
}

